import React from 'react';
import { BadgeCheckIcon, CheckIcon, ChevronRightIcon, CircleDollarSignIcon, CreditCardIcon, FileTextIcon, HeartIcon, LockKeyholeIcon, MinusIcon, PackageCheckIcon, ShieldCheckIcon, SparklesIcon, StarIcon, XIcon } from 'lucide-react';
import { FAQAccordion } from '../components/FAQAccordion';
import { OfferButton } from '../components/OfferButton';
import { SectionHeading } from '../components/SectionHeading';
import { salesPageData as data } from '../data/salesPageData';
import { useScreenInit } from '../useScreenInit';
const trustItems = [{
  icon: FileTextIcon,
  label: '100% PDF'
}, {
  icon: PackageCheckIcon,
  label: 'Entrega inmediata'
}, {
  icon: ShieldCheckIcon,
  label: 'Garantía de 7 días'
}, {
  icon: CreditCardIcon,
  label: 'Mercado Pago'
}];
const solutionChecks = ['Organizar tus prioridades.', 'Reducir el caos mental.', 'Crear hábitos sostenibles.', 'Administrar mejor tu dinero.', 'Diseñar rutinas simples que realmente puedas mantener.'];
const withoutKit = ['Vivís improvisando.', 'Sentís que nunca llegás.', 'Empezás hábitos que abandonás.', 'Gastás sin planificación.', 'Terminás agotada.'];
const withKit = ['Tenés un sistema claro.', 'Sabés qué hacer cada día.', 'Organizás tus prioridades.', 'Mejorás tu relación con el dinero.', 'Vivís con más calma y claridad.'];
function scrollToOffer() {
  document.getElementById('oferta')?.scrollIntoView({
    behavior: 'smooth'
  });
}
function isConfiguredUrl(url: string) {
  return Boolean(url) && !url.includes('[');
}
function MockupImage({
  className = ''
}: {
  className?: string;
}) {
  return <img className={className} src={data.mainMockup} alt={`Mockup del ebook ${data.productName}`} data-id="element-19" />;
}
function Stars() {
  return <div className="flex gap-1 text-[#b6604f]" aria-label="Calificación de 5 estrellas" data-id="element-20">
      {Array.from({
      length: 5
    }).map((_, index) => <StarIcon key={index} aria-hidden="true" className="h-3.5 w-3.5 fill-current" data-id="element-21" />)}
    </div>;
}
export function SalesPage() {
  useScreenInit();
  const checkoutConfigured = isConfiguredUrl(data.checkoutUrl);
  const checkoutHref = checkoutConfigured ? data.checkoutUrl : '#checkout-placeholder';
  return <div className="min-h-screen w-full overflow-x-hidden bg-[#fbf7ef] pb-20 text-[#282820] sm:pb-0" data-id="element-22">
      {/* MARCA */}
      <header className="border-b border-[#e4dccf] bg-[#fbf7ef]" data-id="element-23">
        <div className="mx-auto flex h-[76px] max-w-7xl items-center justify-between px-5 sm:px-8" data-id="element-24">
          <a href="#inicio" className="font-mono-label text-xs font-bold tracking-[0.22em] text-[#38382f]" data-id="element-25">
            {data.brand}
          </a>
          <nav className="hidden items-center gap-7 md:flex" aria-label="Navegación principal" data-id="element-26">
            <a className="text-xs font-bold text-[#65665b] transition hover:text-[#a95848]" href="#incluye" data-id="element-27">
              Qué incluye
            </a>
            <a className="text-xs font-bold text-[#65665b] transition hover:text-[#a95848]" href="#bonos" data-id="element-28">
              Bonos
            </a>
            <a className="text-xs font-bold text-[#65665b] transition hover:text-[#a95848]" href="#opiniones" data-id="element-29">
              Experiencias
            </a>
          </nav>
          <div className="flex items-center gap-3" data-id="element-30">
            <a href="#footer" className="hidden text-xs text-[#65665b] underline underline-offset-4 lg:block" data-id="element-31">
              {data.website}
            </a>
            <OfferButton small href={checkoutHref} data-id="element-32">
              Ver oferta
            </OfferButton>
          </div>
        </div>
      </header>

      <main id="inicio" data-id="element-33">
        {/* PROMESA · MOCKUP · SUBTÍTULO · CTA 1 */}
        <section className="relative border-b border-[#e4dccf] bg-[#f4ede2] px-5 pb-16 pt-12 sm:px-8 sm:pb-24 sm:pt-16" data-id="element-34">
          <div className="mx-auto grid max-w-7xl gap-x-14 lg:grid-cols-[0.92fr_1.08fr] lg:grid-rows-[auto_auto]" data-id="element-35">
            <div className="max-w-xl lg:pt-9" data-id="element-36">
              <p className="mb-5 inline-flex items-center gap-2 rounded-full border border-[#d8b2a7] bg-[#fbf7ef] px-4 py-2 text-[10px] font-extrabold uppercase tracking-[0.16em] text-[#874a3c]" data-id="element-37">
                <SparklesIcon className="h-3.5 w-3.5" aria-hidden="true" data-id="element-38" />
                Ebook práctico para mujeres
              </p>
              <p className="font-mono-label text-[11px] uppercase tracking-[0.16em] text-[#996354]" data-id="element-39">
                {data.productName}
              </p>
              <h1 className="font-editorial mt-4 text-5xl leading-[0.98] text-[#282820] sm:text-6xl lg:text-7xl" data-id="element-40">
                {data.promise}
              </h1>
              <p className="mt-6 max-w-lg text-lg font-medium leading-8 text-[#44453d] sm:text-xl" data-id="element-41">
                {data.headline}
              </p>
              <p className="mt-4 max-w-xl text-sm leading-7 text-[#626359] sm:text-base" data-id="element-42">
                {data.subtitle}
              </p>
            </div>

            <div className="relative mx-auto mt-10 w-full max-w-[620px] lg:col-start-2 lg:row-span-2 lg:mt-0" data-id="element-43">
              <div className="absolute inset-x-[9%] bottom-[5%] top-[16%] rounded-[50%] bg-[#e8c2b7]" aria-hidden="true" data-id="element-44" />
              <div className="relative rounded-[2rem] border border-white/80 bg-[#fdf9f2] p-3 shadow-[0_30px_70px_rgba(79,65,45,0.20)] sm:p-4" data-id="element-45">
                <MockupImage className="aspect-[3/2] w-full rounded-[1.35rem] object-cover" data-id="element-46" />
              </div>
              <p className="mt-4 text-center font-mono-label text-[10px] uppercase tracking-[0.14em] text-[#80776a]" data-id="element-47">
                Tu sistema de calma, en un solo lugar
              </p>
            </div>

            <div className="mt-8 max-w-xl lg:mt-10" data-id="element-48">
              <OfferButton href={checkoutHref} data-id="element-49">
                Quiero acceder a {data.productName}
              </OfferButton>
              <div className="mt-6 grid grid-cols-2 gap-x-4 gap-y-3 border-t border-[#ded2c4] pt-5 sm:grid-cols-4" data-id="element-50">
                {trustItems.map(({
                icon: Icon,
                label
              }) => <div className="flex items-center gap-2 text-[10px] font-bold leading-4 text-[#5b5c51]" key={label} data-id="element-51">
                    <Icon className="h-4 w-4 shrink-0 text-[#a95848]" aria-hidden="true" data-id="element-52" />
                    <span data-id="element-53">{label}</span>
                  </div>)}
              </div>
            </div>
          </div>
        </section>

        {/* PRUEBA SOCIAL */}
        <section className="bg-[#fffaf2] px-5 py-16 sm:px-8 sm:py-24" data-id="element-54">
          <div className="mx-auto max-w-7xl" data-id="element-55">
            <div className="mb-10 flex flex-col justify-between gap-5 md:flex-row md:items-end" data-id="element-56">
              <SectionHeading eyebrow="Experiencias de clientas" title="Un acompañamiento simple para volver a respirar." description="Palabras compartidas por quienes ya incorporaron el kit a su rutina." data-id="element-57" />
              <div className="flex items-center gap-2 self-start rounded-full bg-[#f3e4dc] px-4 py-2 text-xs font-bold text-[#79483d] md:self-auto" data-id="element-58">
                <HeartIcon className="h-4 w-4 fill-current" aria-hidden="true" data-id="element-59" />
                Diseñado para tu día a día
              </div>
            </div>
            <div className="grid gap-4 md:grid-cols-3" data-id="element-60">
              {data.testimonials.map(testimonial => <article key={testimonial.name} className="rounded-[1.5rem] border border-[#e6dbce] bg-white p-6" data-id="element-61">
                  <Stars data-id="element-62" />
                  <blockquote className="mt-5 text-sm leading-7 text-[#525349]" data-id="element-63">
                    “{testimonial.quote}”
                  </blockquote>
                  <p className="mt-6 border-t border-[#eee7db] pt-4 text-xs font-extrabold text-[#36362d]" data-id="element-64">
                    {testimonial.name}{' '}
                    <span className="font-normal text-[#727267]" data-id="element-65">
                      — {testimonial.location}
                    </span>
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* PROBLEMAS Y DOLORES */}
        <section className="bg-[#393a2e] px-5 py-16 sm:px-8 sm:py-24" data-id="element-66">
          <div className="mx-auto max-w-7xl" data-id="element-67">
            <SectionHeading eyebrow="¿Te resulta familiar?" title="No estás fallando. Estás intentando sostener demasiado sin un sistema." description="Mientras trabajás, ya pensás en la comida. Mientras cocinás, recordás cuentas por pagar. Y cuando descansás, tu cabeza sigue repasando pendientes." light data-id="element-68" />
            <div className="mt-12 grid gap-3 sm:grid-cols-2 lg:grid-cols-4" data-id="element-69">
              {data.painPoints.map(pain => <div key={pain} className="flex min-h-[108px] gap-3 rounded-2xl border border-[#595a4b] bg-[#454639] p-5" data-id="element-70">
                  <XIcon className="mt-0.5 h-4 w-4 shrink-0 text-[#e7a99d]" aria-hidden="true" data-id="element-71" />
                  <p className="text-sm leading-6 text-[#f3eee3]" data-id="element-72">{pain}</p>
                </div>)}
            </div>
            <p className="mt-10 max-w-2xl font-editorial text-3xl leading-tight text-[#f3ded6] sm:text-4xl" data-id="element-73">
              ¿Y si hoy fuera la última vez que tenés que pasar por esto?
            </p>
          </div>
        </section>

        {/* SOLUCIÓN */}
        <section id="incluye" className="bg-[#e8d4ca] px-5 py-16 sm:px-8 sm:py-24" data-id="element-74">
          <div className="mx-auto grid max-w-7xl gap-12 lg:grid-cols-2 lg:items-center" data-id="element-75">
            <div className="order-2 lg:order-1" data-id="element-76">
              <SectionHeading eyebrow={`Conocé ${data.productName}`} title="La calma no llega haciendo más. Llega cuando sabés qué hacer." description="The Calm Mind Kit es un sistema diseñado para ayudarte a recuperar el control de tu día a día. No está pensado para que llenes otra agenda más: está pensado para vivir con menos estrés, más organización y mayor claridad mental." data-id="element-77" />
              <div className="mt-8 grid gap-3 sm:grid-cols-2" data-id="element-78">
                {solutionChecks.map(item => <div key={item} className="flex gap-3 rounded-xl border border-[#d5afa3] bg-[#f8ede6] p-4 text-sm font-semibold text-[#49483e]" data-id="element-79">
                    <CheckIcon className="h-5 w-5 shrink-0 text-[#9c4e3e]" aria-hidden="true" data-id="element-80" />
                    {item}
                  </div>)}
              </div>
              <OfferButton className="mt-8" href={checkoutHref} data-id="element-81">
                Quiero conocer la oferta
              </OfferButton>
            </div>
            <div className="order-1 mx-auto w-full max-w-xl lg:order-2" data-id="element-82">
              <div className="overflow-hidden rounded-[2rem] border-[10px] border-[#f8ede6] shadow-[0_24px_50px_rgba(92,54,44,0.18)]" data-id="element-83">
                <MockupImage className="aspect-[4/3] w-full object-cover" data-id="element-84" />
              </div>
              <a href={checkoutHref} className="-mt-1 mx-auto block w-[88%] rounded-b-[1.5rem] bg-[#9b584a] px-5 py-4 text-center text-[11px] font-bold uppercase tracking-[0.12em] text-white transition hover:bg-[#804638] focus:outline-none focus:ring-4 focus:ring-[#dba89b]/60" data-id="element-85">
                Ebook completo + 6 descargables
              </a>
            </div>
          </div>

          <div className="mx-auto mt-16 max-w-7xl border-t border-[#d4aca1] pt-12" data-id="element-86">
            <p className="mb-7 font-mono-label text-[11px] font-bold uppercase tracking-[0.16em] text-[#804438]" data-id="element-87">
              Qué vas a encontrar en el ebook
            </p>
            <div className="grid gap-4 md:grid-cols-5" data-id="element-88">
              {data.modules.map(module => <article key={module.number} className="border-l border-[#b86c5b] pl-4" data-id="element-89">
                  <p className="font-mono-label text-xs text-[#9e5344]" data-id="element-90">
                    {module.number}
                  </p>
                  <h3 className="mt-3 text-sm font-extrabold text-[#37372f]" data-id="element-91">
                    {module.title}
                  </h3>
                  <p className="mt-2 text-xs leading-5 text-[#5d5b52]" data-id="element-92">
                    {module.text}
                  </p>
                </article>)}
            </div>
          </div>
        </section>

        {/* BONOS */}
        <section id="bonos" className="bg-[#fffaf2] px-5 py-16 sm:px-8 sm:py-24" data-id="element-93">
          <div className="mx-auto max-w-7xl" data-id="element-94">
            <SectionHeading centered eyebrow="Además recibís 6 bonos descargables" title="Todo lo que necesitás para empezar desde hoy." description="No más agendas abandonadas, aplicaciones que nunca usás o listas interminables. Cada recurso está pensado para hacer más simple la aplicación del método." data-id="element-95" />
            <div className="mt-12 grid gap-5 md:grid-cols-2 lg:grid-cols-3" data-id="element-96">
              {data.bonuses.map(bonus => <article key={bonus.number} className="overflow-hidden rounded-[1.5rem] border border-[#e6dbce] bg-white" data-id="element-97">
                  <div className="relative border-b border-[#eee3d7] bg-[#f3e4dc] p-5" data-id="element-98">
                    <span className="absolute left-5 top-5 rounded-full bg-[#3c3d31] px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white" data-id="element-99">
                      Bono {bonus.number}
                    </span>
                    <span className="absolute right-5 top-5 rounded-full bg-[#a95848] px-3 py-1 text-[10px] font-extrabold uppercase tracking-[0.12em] text-white" data-id="element-100">
                      Gratis
                    </span>
                    <div className="mt-6 aspect-[4/3] rounded-[1.4rem] bg-[length:300%_200%] bg-no-repeat shadow-[0_12px_24px_rgba(91,64,51,0.14)]" role="img" aria-label={`Portada del bono ${bonus.number}: ${bonus.name}`} style={{
                  backgroundImage: `url(${data.bonusesMockup})`,
                  backgroundPosition: bonus.imagePosition
                }} data-id="element-101" />
                  </div>
                  <div className="p-6" data-id="element-102">
                    <h3 className="text-xl font-extrabold text-[#303027]" data-id="element-103">
                      {bonus.name}
                    </h3>
                    <p className="mt-3 text-sm leading-6 text-[#616257]" data-id="element-104">
                      {bonus.description}
                    </p>
                    <p className="mt-4 border-t border-[#eee7db] pt-4 text-xs font-bold text-[#715347]" data-id="element-105">
                      {bonus.benefit}
                    </p>
                    <div className="mt-4 flex items-center justify-between" data-id="element-106">
                      <span className="text-xs text-[#77776c]" data-id="element-107">
                        Valor individual
                      </span>
                      <span className="text-sm font-extrabold text-[#36362d]" data-id="element-108">
                        {bonus.value}
                      </span>
                    </div>
                  </div>
                </article>)}
            </div>
            <div className="mt-10 rounded-[1.5rem] bg-[#3b3c30] px-6 py-8 text-center sm:px-10" data-id="element-109">
              <p className="font-mono-label text-[11px] font-bold uppercase tracking-[0.15em] text-[#e9b6a9]" data-id="element-110">
                Valor total de los bonos: $15.000
              </p>
              <p className="mt-3 font-editorial text-3xl text-[#fffaf2]" data-id="element-111">
                Hoy los recibís gratis con {data.productName}.
              </p>
              <OfferButton className="mt-6" onClick={scrollToOffer} data-id="element-112">
                Quiero el kit con los bonos
              </OfferButton>
            </div>
          </div>
        </section>

        {/* GATILLOS DE URGENCIA Y ESCASEZ */}
        <section className="border-y border-[#d9b5aa] bg-[#f1ded6] px-5 py-14 sm:px-8" data-id="element-113">
          <div className="mx-auto grid max-w-7xl gap-8 md:grid-cols-[1fr_1.25fr] md:items-center" data-id="element-114">
            <div data-id="element-115">
              <p className="font-mono-label text-[11px] font-bold uppercase tracking-[0.16em] text-[#914b3e]" data-id="element-116">
                Tu compra incluye
              </p>
              <h2 className="font-editorial mt-3 text-4xl leading-tight text-[#352d28]" data-id="element-117">
                Todo lo que recibís hoy con {data.productName}.
              </h2>
            </div>
            <div className="grid gap-3 sm:grid-cols-3" data-id="element-118">
              {[['Precio especial', data.urgency.endDate], ['Incluye', data.urgency.availability], ['Acceso', data.urgency.condition]].map(([label, value]) => <div key={label} className="rounded-xl border border-[#d4a89d] bg-[#f9eee9] p-4" data-id="element-119">
                  <p className="text-[10px] font-extrabold uppercase tracking-[0.13em] text-[#985143]" data-id="element-120">
                    {label}
                  </p>
                  <p className="mt-2 font-mono-label text-[10px] leading-5 text-[#5c443c]" data-id="element-121">
                    {value}
                  </p>
                </div>)}
            </div>
          </div>
          <p className="mx-auto mt-6 max-w-7xl text-xs leading-5 text-[#77594e]" data-id="element-122">
            Una forma simple de empezar a organizarte con más claridad, desde
            hoy.
          </p>
        </section>

        {/* OFERTAS · CTA 2 */}
        <section id="oferta" className="scroll-mt-4 bg-[#fbf7ef] px-5 py-16 sm:px-8 sm:py-24" data-id="element-123">
          <div className="mx-auto max-w-5xl" data-id="element-124">
            <SectionHeading centered eyebrow="Llevate todo hoy" title="Una inversión simple para crear una rutina más clara." description="Ebook completo, seis herramientas descargables y acceso inmediato, en una sola compra." data-id="element-125" />
            <article className="relative mx-auto mt-12 overflow-hidden rounded-[2rem] border border-[#d8b0a5] bg-white shadow-[0_24px_60px_rgba(70,61,44,0.12)]" data-id="element-126">
              <div className="absolute left-1/2 top-0 -translate-x-1/2 rounded-b-xl bg-[#a95848] px-5 py-2 text-[10px] font-extrabold uppercase tracking-[0.13em] text-white" data-id="element-127">
                Oferta más elegida
              </div>
              <div className="grid lg:grid-cols-[0.78fr_1.22fr]" data-id="element-128">
                <div className="bg-[#ede0d6] p-7 pt-14 sm:p-10 sm:pt-16" data-id="element-129">
                  <MockupImage className="aspect-[4/3] w-full rounded-2xl object-cover shadow-[0_15px_30px_rgba(78,55,45,0.16)]" data-id="element-130" />
                  <p className="mt-5 text-center text-xs font-bold text-[#654b41]" data-id="element-131">
                    Formato PDF · Compatible con todos tus dispositivos
                  </p>
                </div>
                <div className="p-7 sm:p-10" data-id="element-132">
                  <p className="font-mono-label text-[11px] font-bold uppercase tracking-[0.14em] text-[#965143]" data-id="element-133">
                    {data.productName}
                  </p>
                  <h3 className="font-editorial mt-2 text-4xl text-[#29291f]" data-id="element-134">
                    Tu kit completo de organización y calma.
                  </h3>
                  <ul className="mt-6 space-y-3" data-id="element-135">
                    {['Ebook completo con 5 módulos prácticos.', '6 bonos descargables incluidos.', 'Acceso inmediato después de confirmar el pago.', `${data.guaranteeDays} días de garantía.`].map(item => <li className="flex gap-3 text-sm text-[#57584d]" key={item} data-id="element-136">
                        <CheckIcon className="h-5 w-5 shrink-0 text-[#9c4e3e]" aria-hidden="true" data-id="element-137" />
                        {item}
                      </li>)}
                  </ul>
                  <div className="mt-7 border-y border-[#e8dfd4] py-6" data-id="element-138">
                    <div className="flex items-end gap-3" data-id="element-139">
                      <div data-id="element-140">
                        <p className="text-xs font-bold text-[#77766a]" data-id="element-141">
                          Valor regular
                        </p>
                        <p className="mt-1 text-lg text-[#858579] line-through" data-id="element-142">
                          {data.regularPrice}
                        </p>
                      </div>
                      <div className="border-l border-[#dfd6ca] pl-4" data-id="element-143">
                        <p className="text-xs font-bold text-[#9e5141]" data-id="element-144">
                          Precio promocional
                        </p>
                        <p className="mt-1 text-3xl font-extrabold tracking-tight text-[#333329]" data-id="element-145">
                          {data.promotionalPrice}
                        </p>
                      </div>
                    </div>
                    <p className="mt-4 inline-flex rounded-full bg-[#f3e4dc] px-3 py-1.5 text-[10px] font-extrabold uppercase tracking-[0.11em] text-[#934e40]" data-id="element-146">
                      Ahorrás un {data.savings}
                    </p>
                  </div>
                  <OfferButton href={checkoutHref} className="mt-7 w-full sm:w-full" data-id="element-147">
                    Quiero {data.productName} ahora
                  </OfferButton>
                  {!checkoutConfigured ? <p id="checkout-placeholder" className="mt-3 text-center font-mono-label text-[10px] leading-5 text-[#9a5b4d]" data-id="element-148">
                      [PEGAR LINK DE CHECKOUT] — configurá el enlace para
                      habilitar la compra.
                    </p> : null}
                  <div className="mt-6 flex flex-wrap gap-x-5 gap-y-2 text-[10px] font-bold text-[#68685d]" data-id="element-149">
                    <span className="inline-flex items-center gap-1.5" data-id="element-150">
                      <ShieldCheckIcon className="h-4 w-4 text-[#718066]" aria-hidden="true" data-id="element-151" />{' '}
                      Garantía de {data.guaranteeDays} días
                    </span>
                    <span className="inline-flex items-center gap-1.5" data-id="element-152">
                      <LockKeyholeIcon className="h-4 w-4 text-[#718066]" aria-hidden="true" data-id="element-153" />{' '}
                      Pago seguro
                    </span>
                    <span data-id="element-154">{data.paymentMethods}</span>
                  </div>
                </div>
              </div>
            </article>
          </div>
        </section>

        {/* UPSELL — oculto hasta completar información real */}
        {!data.upsell.enabled ? <section aria-hidden="true" className="hidden" id="upsell" data-id="element-155">
            <h2 data-id="element-156">{data.upsell.name}</h2>
            <p data-id="element-157">{data.upsell.description}</p>
          </section> : null}

        {/* TESTIMONIOS */}
        <section id="opiniones" className="bg-[#ece2d2] px-5 py-16 sm:px-8 sm:py-24" data-id="element-158">
          <div className="mx-auto max-w-7xl" data-id="element-159">
            <SectionHeading centered eyebrow="Testimonios" title="Pequeños cambios que hacen lugar a días más claros." description="Experiencias compartidas por clientas de The Calm Mind Kit." data-id="element-160" />
            <div className="mt-12 grid gap-5 md:grid-cols-3" data-id="element-161">
              {data.testimonials.map(testimonial => <article key={`full-${testimonial.name}`} className="rounded-[1.5rem] bg-[#fffaf3] p-7" data-id="element-162">
                  <div className="flex items-start justify-between gap-4" data-id="element-163">
                    <div className="flex h-11 w-11 items-center justify-center rounded-full bg-[#d9aa9c] font-editorial text-xl text-white" aria-hidden="true" data-id="element-164">
                      {testimonial.name.charAt(0)}
                    </div>
                    <Stars data-id="element-165" />
                  </div>
                  <blockquote className="mt-6 text-sm leading-7 text-[#4e4f44]" data-id="element-166">
                    “{testimonial.quote}”
                  </blockquote>
                  <p className="mt-6 text-xs font-extrabold text-[#39392f]" data-id="element-167">
                    {testimonial.name}{' '}
                    <span className="font-normal text-[#6d6c61]" data-id="element-168">
                      · {testimonial.location}
                    </span>
                  </p>
                  <p className="mt-1 text-[10px] font-bold uppercase tracking-[0.1em] text-[#a35b4c]" data-id="element-169">
                    The Calm Mind Kit
                  </p>
                </article>)}
            </div>
            <div className="mt-5 rounded-xl border border-dashed border-[#c9a699] bg-[#f7eee7] p-5 text-center font-mono-label text-[10px] leading-5 text-[#875a4f]" data-id="element-170">
              AGREGAR AQUÍ TESTIMONIO REAL Y VERIFICABLE
            </div>
          </div>
        </section>

        {/* GARANTÍA */}
        <section className="bg-[#fffaf2] px-5 py-16 sm:px-8 sm:py-24" data-id="element-171">
          <div className="mx-auto grid max-w-5xl gap-9 rounded-[2rem] border border-[#dbe0cf] bg-[#f0f2e8] p-7 sm:p-12 md:grid-cols-[180px_1fr] md:items-center" data-id="element-172">
            <div className="mx-auto flex h-36 w-36 items-center justify-center rounded-full border-[8px] border-[#e2bcaf] bg-[#fffaf2] text-center shadow-sm" data-id="element-173">
              <div data-id="element-174">
                <ShieldCheckIcon className="mx-auto h-10 w-10 text-[#6c795f]" aria-hidden="true" data-id="element-175" />
                <p className="mt-1 text-xs font-extrabold text-[#4f5845]" data-id="element-176">
                  {data.guaranteeDays} DÍAS
                </p>
              </div>
            </div>
            <div data-id="element-177">
              <p className="font-mono-label text-[11px] font-bold uppercase tracking-[0.16em] text-[#68755b]" data-id="element-178">
                Compra con tranquilidad
              </p>
              <h2 className="font-editorial mt-3 text-4xl text-[#303127]" data-id="element-179">
                Garantía de {data.guaranteeDays} días, sin vueltas.
              </h2>
              <p className="mt-5 max-w-2xl text-sm leading-7 text-[#5c6054]" data-id="element-180">
                Tu compra está protegida por una garantía de{' '}
                {data.guaranteeDays} días. Podés ingresar al material, revisar
                su contenido y comprobar que coincide con lo presentado en esta
                página. Si el material no coincide con lo prometido, escribinos
                dentro de ese plazo y gestionaremos tu solicitud de acuerdo con
                los términos de la garantía.
              </p>
              <a className="mt-5 inline-flex items-center gap-2 text-sm font-bold text-[#526047] underline underline-offset-4" href={`mailto:${data.supportEmail}`} data-id="element-181">
                {data.supportEmail}{' '}
                <ChevronRightIcon className="h-4 w-4" aria-hidden="true" data-id="element-182" />
              </a>
            </div>
          </div>
        </section>

        {/* PREGUNTAS FRECUENTES */}
        <section className="bg-[#fbf7ef] px-5 py-16 sm:px-8 sm:py-24" data-id="element-183">
          <div className="mx-auto max-w-7xl" data-id="element-184">
            <SectionHeading centered eyebrow="Preguntas frecuentes" title="Todo lo que necesitás saber antes de empezar." data-id="element-185" />
            <div className="mt-10" data-id="element-186">
              <FAQAccordion items={data.faq} data-id="element-187" />
            </div>
          </div>
        </section>

        {/* GATILLOS DE DOLOR Y URGENCIA */}
        <section className="bg-[#34352b] px-5 py-16 sm:px-8 sm:py-24" data-id="element-188">
          <div className="mx-auto max-w-7xl" data-id="element-189">
            <SectionHeading centered eyebrow="La decisión de hoy" title="¿Cuánto te está costando no organizarte?" description="Cada día que pasa sin un sistema, perdés tiempo intentando recordar todo, postergás objetivos importantes y gastás energía tomando decisiones constantemente." light data-id="element-190" />
            <div className="mt-12 grid gap-5 md:grid-cols-2" data-id="element-191">
              <div className="rounded-[1.5rem] border border-[#626355] bg-[#414237] p-7 sm:p-9" data-id="element-192">
                <div className="flex items-center gap-3" data-id="element-193">
                  <MinusIcon className="h-5 w-5 text-[#e19d91]" aria-hidden="true" data-id="element-194" />
                  <h3 className="font-mono-label text-xs font-bold uppercase tracking-[0.16em] text-[#f0c1b5]" data-id="element-195">
                    Sin el producto
                  </h3>
                </div>
                <ul className="mt-6 space-y-4" data-id="element-196">
                  {withoutKit.map(item => <li key={item} className="flex gap-3 text-sm text-[#e9e4d9]" data-id="element-197">
                      <XIcon className="h-4 w-4 shrink-0 text-[#de9d91]" aria-hidden="true" data-id="element-198" />{' '}
                      {item}
                    </li>)}
                </ul>
              </div>
              <div className="rounded-[1.5rem] border border-[#76826a] bg-[#4d5544] p-7 sm:p-9" data-id="element-199">
                <div className="flex items-center gap-3" data-id="element-200">
                  <BadgeCheckIcon className="h-5 w-5 text-[#d8e0bf]" aria-hidden="true" data-id="element-201" />
                  <h3 className="font-mono-label text-xs font-bold uppercase tracking-[0.16em] text-[#e3ebce]" data-id="element-202">
                    Con {data.productName}
                  </h3>
                </div>
                <ul className="mt-6 space-y-4" data-id="element-203">
                  {withKit.map(item => <li key={item} className="flex gap-3 text-sm text-[#f5f5eb]" data-id="element-204">
                      <CheckIcon className="h-4 w-4 shrink-0 text-[#dfe8c5]" aria-hidden="true" data-id="element-205" />{' '}
                      {item}
                    </li>)}
                </ul>
              </div>
            </div>
            <p className="mx-auto mt-10 max-w-3xl text-center font-editorial text-3xl leading-tight text-[#f1ded6] sm:text-4xl" data-id="element-206">
              No necesitás una vida perfecta. Necesitás un sistema que te
              acompañe en la vida que ya tenés.
            </p>
          </div>
        </section>

        {/* CTA 3 */}
        <section className="bg-[#e6cabe] px-5 py-16 sm:px-8 sm:py-24" data-id="element-207">
          <div className="mx-auto grid max-w-5xl gap-9 lg:grid-cols-[0.75fr_1.25fr] lg:items-center" data-id="element-208">
            <MockupImage className="mx-auto aspect-[4/3] w-full max-w-md rounded-[1.75rem] border-8 border-[#f9eee7] object-cover shadow-[0_22px_45px_rgba(84,53,43,0.2)]" data-id="element-209" />
            <div data-id="element-210">
              <p className="font-mono-label text-[11px] font-bold uppercase tracking-[0.16em] text-[#904a3d]" data-id="element-211">
                {data.productName}
              </p>
              <h2 className="font-editorial mt-3 text-5xl leading-[1.03] text-[#322c28]" data-id="element-212">
                {data.promise}
              </h2>
              <p className="mt-5 max-w-xl text-base leading-7 text-[#5c514a]" data-id="element-213">
                Ebook completo, 6 bonos descargables, acceso inmediato y una
                garantía de {data.guaranteeDays} días para que puedas revisar el
                material con tranquilidad.
              </p>
              <div className="mt-6 flex flex-wrap gap-3" data-id="element-214">
                <span className="rounded-full bg-[#f8ece5] px-4 py-2 text-xs font-bold text-[#6f4c43]" data-id="element-215">
                  {data.promotionalPrice}
                </span>
                <span className="rounded-full bg-[#f8ece5] px-4 py-2 text-xs font-bold text-[#6f4c43]" data-id="element-216">
                  Mercado Pago
                </span>
                <span className="rounded-full bg-[#f8ece5] px-4 py-2 text-xs font-bold text-[#6f4c43]" data-id="element-217">
                  Entrega inmediata
                </span>
              </div>
              <OfferButton href={checkoutHref} className="mt-8" data-id="element-218">
                Quiero empezar ahora
              </OfferButton>
              {!checkoutConfigured ? <p className="mt-3 font-mono-label text-[10px] text-[#8f564a]" data-id="element-219">
                  [PEGAR LINK DE CHECKOUT]
                </p> : null}
            </div>
          </div>
        </section>
      </main>

      {/* PIE DE PÁGINA */}
      <footer id="footer" className="bg-[#282920] px-5 pb-28 pt-12 text-[#eee9df] sm:px-8 sm:pb-12" data-id="element-220">
        <div className="mx-auto grid max-w-7xl gap-10 md:grid-cols-[1.2fr_0.8fr_1fr]" data-id="element-221">
          <div data-id="element-222">
            <p className="font-mono-label text-sm font-bold tracking-[0.2em]" data-id="element-223">
              {data.brand}
            </p>
            <p className="mt-4 max-w-sm text-sm leading-6 text-[#cbc9bb]" data-id="element-224">
              {data.productName} es un producto digital de uso personal. La
              reproducción, distribución o comercialización sin autorización
              está prohibida.
            </p>
          </div>
          <div data-id="element-225">
            <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#eab5a8]" data-id="element-226">
              Contacto
            </p>
            <a href="#footer" className="mt-4 block text-sm text-[#eee9df] underline underline-offset-4" data-id="element-227">
              {data.website}
            </a>
            <a href={`mailto:${data.supportEmail}`} className="mt-3 block text-sm text-[#eee9df] underline underline-offset-4" data-id="element-228">
              {data.supportEmail}
            </a>
          </div>
          <div data-id="element-229">
            <p className="text-xs font-extrabold uppercase tracking-[0.12em] text-[#eab5a8]" data-id="element-230">
              Información legal
            </p>
            <div className="mt-4 grid gap-3 text-sm text-[#d3d0c5]" data-id="element-231">
              <a href="#footer" className="underline underline-offset-4" data-id="element-232">
                [TÉRMINOS Y CONDICIONES]
              </a>
              <a href="#footer" className="underline underline-offset-4" data-id="element-233">
                [POLÍTICA DE PRIVACIDAD]
              </a>
              <a href="#footer" className="underline underline-offset-4" data-id="element-234">
                [POLÍTICA DE REEMBOLSO]
              </a>
            </div>
          </div>
        </div>
        <div className="mx-auto mt-12 max-w-7xl border-t border-[#4b4c40] pt-6 text-xs leading-6 text-[#aaa99e]" data-id="element-235">
          <p data-id="element-236">
            © {new Date().getFullYear()} {data.brand}. Todos los derechos
            reservados.
          </p>
          <p className="mt-2 max-w-4xl" data-id="element-237">
            El contenido de este producto tiene fines educativos y de
            organización personal. No reemplaza asesoramiento profesional,
            financiero, médico, psicológico o de cualquier otra especialidad.
          </p>
        </div>
      </footer>

      <div className="fixed inset-x-0 bottom-0 z-30 border-t border-[#d6c1b8] bg-[#fffaf2]/95 p-3 backdrop-blur sm:hidden" data-id="element-238">
        <a href={checkoutHref} className="flex w-full items-center justify-center gap-2 rounded-full bg-[#a95848] px-5 py-4 text-xs font-extrabold uppercase tracking-[0.12em] text-white shadow-lg transition hover:bg-[#873f32] focus:outline-none focus:ring-4 focus:ring-[#dba89b]/50" data-id="element-239">
          Ver oferta{' '}
          <CircleDollarSignIcon className="h-4 w-4" aria-hidden="true" data-id="element-240" />
        </a>
      </div>
    </div>;
}